import { Box, Button, Tabs } from '@mantine/core';
import { cloneDeep, omit } from 'lodash';
import { useEffect, useState } from 'react';

import { Declaration, EnvironmentCountries } from '@e-origin/shared';

import { useSaveFinalizeDataToAllDeclarations } from '@hooks/batch';
import { useFormWithNameInputProp } from '@hooks/useFormWithNameInputProp';
import { useSettings } from '@hooks/useSettings';
import { classes } from '@styles';
import { isHighValueDeclaration, isVatDeferredPaymentInvalid } from '@utils';
import { beforeSendingBatchFormIntializer, BeforeSendingFormData } from './beforeSendingBatchFormIntializer';
import { FinalizeCommodityCodes } from './CommodityCodes';
import { FinalizeCustomsTreatment } from './FinalizeCustomsTreatment';
import { FinalizeDeclarationDocuments } from './FinalizeDeclarationDocuments';
import { FinalizeGlobalParameter } from './FinalizeGlobalParameter';
import { FinalizeProcedure } from './FinalizeProcedure';
import { FinalizeTransport } from './FinalizeTransport';
import { FinalizeWriteOff } from './FinalizeWriteOff';

interface BeforeSendingBatchProps {
  declaration: Declaration;
  brokerId: string;
  onHide(): void;
  disabled: boolean;
}

// refactor: merge BeforeSendingBatch and FinalizeBatch components
export const BeforeSendingBatch: React.FC<BeforeSendingBatchProps> = ({ declaration, brokerId, onHide, disabled }) => {
  const [isSaving, setIsSaving] = useState(false);
  const { data: settings } = useSettings();

  const saveFinalizeDataToAllDeclarationsMutation = useSaveFinalizeDataToAllDeclarations();

  const isExport = declaration?.generalInfo?.isExport;

  const form = useFormWithNameInputProp<BeforeSendingFormData>({
    initialValues: beforeSendingBatchFormIntializer(declaration),
  });

  useEffect(() => {
    form.setValues(beforeSendingBatchFormIntializer(declaration));
  }, [declaration]);

  const vatDeferredInvalid =
    settings.country === EnvironmentCountries.NL &&
    isHighValueDeclaration(declaration?.generalInfo?.group) &&
    isVatDeferredPaymentInvalid(form.values.messageInfo);

  // eslint-disable-next-line max-statements
  const saveData = async (values: BeforeSendingFormData) => {
    setIsSaving(true);

    const partialDeclaration = cloneDeep(
      omit(values, ['commodityCodes', 'declarationGoods', 'customsProcedureCurrent', 'customsProcedurePrevious', 'warehouse']),
    );
    partialDeclaration.goodShipmentGlobal.customsProcedure = (values.customsProcedureCurrent || '') + (values.customsProcedurePrevious || '');

    if (partialDeclaration.transportInformation?.transportEquipment?.seals?.length) {
      partialDeclaration.transportInformation.transportEquipment.seals = partialDeclaration.transportInformation.transportEquipment.seals.filter(
        (s) => !!s.identificationNumber,
      );
    }

    if (partialDeclaration.transportInformation?.transportEquipment?.containers.length) {
      partialDeclaration.transportInformation.transportEquipment.containers = partialDeclaration.transportInformation.transportEquipment.containers.filter(
        (s) => !!s.identificationNumber,
      );
    }

    if (partialDeclaration.transportInformation?.countryOfRoutingOfConsignment?.length) {
      partialDeclaration.transportInformation.countryOfRoutingOfConsignment = partialDeclaration.transportInformation.countryOfRoutingOfConsignment.filter(
        (c) => !!c.country,
      );
    }

    if (values.declarationGoods?.additionalProcedure) {
      values.declarationGoods.additionalProcedure = values.declarationGoods.additionalProcedure.filter((procedure) => procedure !== '');
    }

    await saveFinalizeDataToAllDeclarationsMutation.mutateAsync({
      batchId: declaration.messageInfo.batch,
      partialDeclaration,
      partialGoodsShipmentItem: values.declarationGoods,
      commodityCodes: values.commodityCodes.map((c) => ({
        ...c,
        additionalReference: (c.additionalReference || []).filter((sd) => sd.selected).map((sd) => sd.document),
      })),
    });
    setIsSaving(false);
  };

  const onSubmit = async (values: BeforeSendingFormData) => {
    await saveData(values);
    onHide();
  };

  return (
    <Box data-pw="finalize-batch-modal-content">
      <Tabs defaultValue={settings?.isForwarder ? 'customs' : 'global'} mx={15} mb={15}>
        <Tabs.List>
          {!settings?.isForwarder && (
            <>
              <Tabs.Tab value="global" data-pw="finalize-global-parameter-tab">
                Global Parameter
              </Tabs.Tab>
              <Tabs.Tab value="procedure" data-pw="finalize-procedure-tab">
                Procedure
              </Tabs.Tab>
              <Tabs.Tab value="transport" data-pw="finalize-transport-tab">
                Transport
              </Tabs.Tab>
            </>
          )}
          {settings.country === EnvironmentCountries.BE && !isExport && (
            <Tabs.Tab value="writeOff" data-pw="finalize-write-off-tab">
              Write-off
            </Tabs.Tab>
          )}
          <Tabs.Tab value="customs" data-pw="finalize-customs-treatment-tab">
            Customs Treatment
          </Tabs.Tab>
          {!settings?.isForwarder && (
            <Tabs.Tab value="documents" data-pw="finalize-documents-tab">
              Documents
            </Tabs.Tab>
          )}
          <Tabs.Tab value="commodity" data-pw="finalize-commodity-codes-tab">
            Commodity code
          </Tabs.Tab>
        </Tabs.List>

        {!settings?.isForwarder && (
          <>
            <Tabs.Panel value="global" py={15} px={50} data-pw="finalize-documents-global-parameter-container">
              <FinalizeGlobalParameter form={form} disabled={disabled} declaration={declaration} brokerId={brokerId} />
            </Tabs.Panel>

            <Tabs.Panel value="procedure" py={15} px={50} data-pw="finalize-documents-procedure-container">
              <FinalizeProcedure
                form={form}
                disabled={disabled}
                isExport={isExport}
                onSave={(newGroup) => saveData({ ...form.values, generalInfo: { ...form.values.generalInfo, group: newGroup } })}
              />
            </Tabs.Panel>

            <Tabs.Panel value="transport" py={15} px={50} data-pw="finalize-transport-container">
              <FinalizeTransport form={form} declaration={declaration} disabled={disabled} />
            </Tabs.Panel>
            <Tabs.Panel value="documents" py={15} px={50} data-pw="finalize-documents-container">
              <FinalizeDeclarationDocuments declaration={declaration} form={form} disabled={disabled} />
            </Tabs.Panel>
          </>
        )}
        {settings.country === EnvironmentCountries.BE && !isExport && (
          <Tabs.Panel value="writeOff" py={15} px={50}>
            <FinalizeWriteOff declaration={declaration} disabled={disabled} />
          </Tabs.Panel>
        )}
        <Tabs.Panel value="customs" py={15} px={50} data-pw="finalize-customs-treatment-container">
          <FinalizeCustomsTreatment form={form} declaration={declaration} disabled={disabled} />
        </Tabs.Panel>

        <Tabs.Panel value="commodity" py={15} px={50}>
          <FinalizeCommodityCodes form={form} declaration={declaration} disabled={disabled} />
        </Tabs.Panel>
      </Tabs>
      <Box className={classes.footer}>
        <Button type="button" onClick={onHide} size="lg" variant="transparent">
          Cancel
        </Button>
        {!disabled && (
          <Button
            data-pw="submit-btn"
            type="button"
            onClick={() => onSubmit(form.values)}
            loading={isSaving}
            disabled={vatDeferredInvalid}
            variant="filled"
            size="lg"
          >
            Save
          </Button>
        )}
      </Box>
    </Box>
  );
};
